'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from '@/src/i18n/navigation';
import type { AreaResponse } from '@/src/lib/api';
import { useFilters } from '@/src/lib/filters';
import { useCurrentArea, useCurrentResolvedSearch, useCurrentUser, useUpdateRecentSearch } from '@/src/lib/hooks/query';
import { useMapBorderState, useMapPosition } from '@/src/lib/hooks/map';
import type { MapState } from '@/src/lib/hooks/map/mapStateKeys';
import { useDebounce } from 'ahooks';
import { isNearMeSlugCandidate } from '@/src/lib/seo/near-me';
import { buildRecentSearchBody, getAreaBounds, type RecentSearchGeo } from '@/src/lib/savedSearches';

// Collapses a burst of filter changes (modal apply, rapid chip toggles) into one PUT.
const RECENT_SEARCH_DEBOUNCE_MS = 1000;

const DEFAULT_SEARCH_SLUG = 'apartments-for-rent';

// Geo precedence mirrors legacy; `areas` is dropped for university areas (HPWEB-5750: unsearchable ids).
function resolveGeo(
  area: AreaResponse | undefined,
  mapPosition: MapState | undefined,
  showBorder: boolean,
): RecentSearchGeo | null {
  const areaBounds = getAreaBounds(area);

  if (showBorder && area?.id && areaBounds) {
    if (area.type === 'university') {
      return { areaId: null, bounds: areaBounds };
    }
    return {
      areaId: area.id,
      lat: area.coordinates?.lat,
      lon: area.coordinates?.lon,
      bounds: areaBounds,
    };
  }

  const viewport = mapPosition?.bounds;
  if (viewport) {
    return {
      areaId: null,
      bounds: {
        minLat: viewport.southLat,
        maxLat: viewport.northLat,
        minLon: viewport.westLng,
        maxLon: viewport.eastLng,
      },
    };
  }

  if (areaBounds) {
    return { areaId: null, bounds: areaBounds };
  }

  return null;
}

/** Persists the search via PUT /v2/user/recentSearch/update after each filter interaction (HP-8422); renders nothing. */
export function RecentSearchPersistence() {
  const pathname = usePathname() ?? '/';
  const { filters, interactionSeq = 0 } = useFilters();
  const { data: currentUser } = useCurrentUser();
  const { data: currentArea } = useCurrentArea();
  const { data: resolved } = useCurrentResolvedSearch();
  const mapPosition = useMapPosition();
  const { showBorder } = useMapBorderState();
  const { mutate: updateRecentSearch } = useUpdateRecentSearch();

  const segments = pathname.split('/').filter(Boolean);
  const isNearMeRoute = segments.length === 1 && isNearMeSlugCandidate(segments[0]);
  const routeSlug = isNearMeRoute ? undefined : segments[1];
  const isHdpRoute = routeSlug === 'pad' || routeSlug === 'building';
  // Near-me and HDP path segments aren't search slugs — fall back to the resolved SRP slug.
  const searchSlug = (!isHdpRoute && routeSlug) || resolved?.resolvedSlug || DEFAULT_SEARCH_SLUG;

  // Read at send time via ref so map/auth cache writes never re-trigger the send effect.
  const sendContextRef = useRef({
    filters,
    currentArea,
    mapPosition,
    showBorder,
    searchSlug,
    identityResolved: false,
    loggedIn: false,
  });
  sendContextRef.current = {
    filters,
    currentArea,
    mapPosition,
    showBorder,
    searchSlug,
    identityResolved: currentUser !== undefined,
    loggedIn: currentUser?.loggedIn ?? false,
  };

  const lastHandledSeqRef = useRef(0);
  const lastSentRef = useRef<string | null>(null);

  const sendRecentSearch = useCallback(
    (seq: number) => {
      if (seq === 0 || seq <= lastHandledSeqRef.current) return;
      const ctx = sendContextRef.current;
      if (!ctx.identityResolved) return;

      lastHandledSeqRef.current = seq;
      if (!ctx.loggedIn) return;

      const geo = resolveGeo(ctx.currentArea, ctx.mapPosition, ctx.showBorder);
      if (!geo) return;

      const body = buildRecentSearchBody({ filters: ctx.filters, searchSlug: ctx.searchSlug, geo });
      const serialized = JSON.stringify(body);
      if (serialized === lastSentRef.current) return;

      lastSentRef.current = serialized;
      updateRecentSearch(body);
    },
    [updateRecentSearch],
  );

  const debouncedSeq = useDebounce(interactionSeq, { wait: RECENT_SEARCH_DEBOUNCE_MS });
  useEffect(() => {
    sendRecentSearch(debouncedSeq);
  }, [currentUser, debouncedSeq, sendRecentSearch]);

  // Flush an interaction still inside the debounce window when the shell unmounts.
  const latestSeqRef = useRef(interactionSeq);
  latestSeqRef.current = interactionSeq;
  const sendRef = useRef(sendRecentSearch);
  sendRef.current = sendRecentSearch;
  useEffect(
    () => () => {
      sendRef.current(latestSeqRef.current);
    },
    [],
  );

  return null;
}
